import { NewNotificationProps } from './new-notification-modal-types';
import styles from './styles.module.scss';
import { UserRole, UserStatus } from 'api/generated';
import { useAuth } from 'features/auth/contexts/auth';
import { useEmployeesQuery } from 'features/employees/api/repositories/use-employees.query';
import { useSendNotificationMutation } from 'features/notifications/api/repositories/use-send-notification.mutation';
import 'features/shared/styles.css';
import { FC, useEffect, useState } from 'react';
import Select, { components, OptionProps, SingleValueProps } from 'react-select';
import { useThemeMode } from 'theme/contexts/theme';

type FormState = {
  userId?: number;
  message?: string;
  sourceId?: string;
};
type OptionType = { value: number; label: string; role: string };

const CustomSingleValue = (props: SingleValueProps<OptionType, false>) => {
  return (
    <components.SingleValue {...props}>
      <span className={`d-flex gap-2 flex-nowrap ${styles.singleInput}`}>
        <span>{props.data.label}</span>
        <span className={styles.roleSelectItem}>{props.data.role}</span>
      </span>
    </components.SingleValue>
  );
};

const CustomOption = (props: OptionProps<OptionType>) => {
  return (
    <components.Option {...props}>
      <span className="d-flex gap-2 flex-nowrap">
        <span>{props.data.label}</span>
        <span className={styles.roleSelectItem}>{props.data.role}</span>
      </span>
    </components.Option>
  );
};

export const ModalForm: FC<NewNotificationProps> = ({ onClose, sourceId, sourceType, sourceUrl }) => {
  const [formState, setFormState] = useState<FormState>();
  const [options, setOptions] = useState<OptionType[]>([]);
  const { mode } = useThemeMode();

  const { currentUser } = useAuth();

  const { data: users, isLoading: isLoadingUsers } = useEmployeesQuery({
    skipPagination: true,
    status: [UserStatus.Active]
    //role: [UserRole.Technician]
  });

  const { mutate: mutateSend, isLoading: isLoadingSend } = useSendNotificationMutation();
  useEffect(() => {
    setFormState((prev) => ({ ...prev, userId: undefined }));
    if (!users?.data) {
      return;
    }
    const currentOptions: OptionType[] = users.data
      .filter((x) => x.id && x.name && x.id != currentUser?.userId)
      .map((x) => {
        return {
          value: x.id ?? -1,
          label: x.name ?? '',
          role: x.role ?? ''
        };
      });
    setOptions(currentOptions);
  }, [users]);

  const handleSelectSales = (option?: OptionType | null) => {
    setFormState((prev) => ({ ...prev, userId: option?.value ?? undefined }));
  };

  useEffect(() => {
    setFormState((prev) => ({ ...prev, sourceId }));
  }, [sourceId]);

  const handleSend = () => {
    mutateSend(
      {
        body: formState?.message!,
        sourceId: sourceId!,
        userIds: [formState!.userId!],
        senderId: currentUser!.userId!,
        sourceType: sourceType!,
        sourceUrl: sourceUrl
      },
      {
        onSuccess: () => {
          onClose();
        }
      }
    );
  };

  const isValidFormState = (formState?.message?.length ?? 0) > 0 && formState?.userId;
  const isDarkTheme = mode === 'dark';

  return (
    <div className={`container ${styles.editContainer}`}>
      <div className="row">
        <div className="col-12 mt-10">
          <label className="form-label">User to receive</label>
          <Select
            filterOption={(option, inputValue) => option.data.label.toLowerCase().includes(inputValue.toLowerCase())}
            placeholder={isLoadingUsers ? 'Please wait...' : 'Select...'}
            isClearable
            classNamePrefix={'Select'}
            className={`${styles.selectContainer}`}
            options={options}
            value={options?.find((x) => x.value == formState?.userId)}
            onChange={(val) => {
              handleSelectSales(val);
            }}
            components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({
                ...base,
                backgroundColor: isDarkTheme ? '#1b1b29' : 'white',
                color: isDarkTheme ? 'white' : 'black'
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused && isDarkTheme ? '#1e1e2d' : state.isFocused ? '#F5F8FA' : undefined,
                color: isDarkTheme ? 'var(--bs-gray-700)' : 'black'
              })
            }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 mt-10">
          <label className="form-label">Message</label>
          <textarea
            className="form-control form-control-solid border-0 pe-10 resize-none"
            rows={4}
            value={formState?.message ?? ''}
            onChange={({ target }) => setFormState((prev) => ({ ...prev, message: target.value }))}
          />
        </div>
      </div>
      <div className="row mt-10">
        <div className="col-12">
          <div className="d-flex justify-content-center align-items-center gap-10">
            <div className="d-flex flex-row justify-content-left gap-4">
              <button
                type="button"
                onClick={handleSend}
                className="btn btn-primary"
                disabled={isLoadingUsers || !isValidFormState || isLoadingSend}
              >
                {!isLoadingUsers && !isLoadingSend && <>Send</>}
                {(isLoadingUsers || isLoadingSend) && (
                  <span className="indicator-progress" style={{ display: 'block' }}>
                    Please wait... <span className="spinner-border spinner-border-sm align-middle"></span>
                  </span>
                )}
              </button>
              <button type="button" onClick={() => onClose()} className="btn btn-light-primary">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
